/**
 * Der Pflegebox-Warenkorb als reine Funktionen — geteilt zwischen Patientenportal und
 * Leistungserbringer-Portal.
 *
 * <p>Bis 08/2026 lag diese Rechnerei im NGXS-`ProduktCartState` der Selfservice-App. Damit war sie
 * für das Leistungserbringer-Portal unerreichbar, und genau deshalb band dessen Cockpit-Reiter das
 * Patientenportal als iframe ein. Hier stehen die Regeln jetzt einmal — an ihnen hängt Geld: was
 * über der Pauschale liegt, erstattet die Kasse nicht.
 *
 * <p>**Ohne Meldungstexte.** Die Funktionen liefern einen `fehler`-Code; welchen Satz der Nutzer
 * dazu liest, entscheidet die App (`libs/shared` kennt keine Snackbar-Texte).
 */

/**
 * Monatlicher Höchstbetrag für zum Verbrauch bestimmte Pflegehilfsmittel (PG 54) nach
 * § 40 Abs. 2 SGB XI — **brutto**.
 *
 * Dieselbe Zahl steht serverseitig in `PflegeboxProductService.MONATLICHER_HOECHSTBETRAG_BRUTTO`;
 * hebt der Gesetzgeber sie an (zuletzt 2025 von 40 auf 42 €), müssen beide Stellen nachgezogen
 * werden. Das Backend ist die verbindliche Prüfung, diese hier verhindert nur, dass der Nutzer
 * etwas zusammenstellt, das erst beim Absenden abgelehnt wird.
 */
export const PFLEGEBOX_PAUSCHALE = 42

/**
 * Die Felder eines Pflegebox-Produkts, die der Warenkorb wirklich liest.
 *
 * <p>Bewusst **strukturell** statt aus einer der beiden generierten Specs: die Apps haben je eigene
 * `*-model-gen.ts`, und `libs/shared` darf aus keiner davon importieren. Beide generierten Typen
 * sind hierauf zuweisbar.
 */
export interface PflegeboxProduktLike {
  id?: string
  name?: string
  pictureUrl?: string | null
  productType?: string
  quantityUnit?: string
  grossPrice?: number
  maxAllowedQuantity?: number | null
  sizeMaterialAndQuantityOptions?: PflegeboxSkuLike[] | null
}

/** Eine wählbare Ausprägung eines Produkts (Größe · Material · Gebindegröße). */
export interface PflegeboxSkuLike {
  productMatchingId?: string
  quantity?: number
  size?: string
  material?: string
  hilfsmittelnummer?: string
}

/** Eine Zeile im Warenkorb. */
export interface PflegeboxWarenkorbPosition {
  produkt: PflegeboxProduktLike
  sku: PflegeboxSkuLike
  menge: number
}

/**
 * Warum eine Änderung abgelehnt wurde. Der Aufrufer übersetzt den Code in einen Satz — im
 * Patientenportal anders als im Leistungserbringer-Portal.
 */
export type WarenkorbFehler = 'PAUSCHALE_UEBERSCHRITTEN' | 'HOECHSTMENGE_ERREICHT'

export interface WarenkorbErgebnis {
  warenkorb: PflegeboxWarenkorbPosition[]
  /** Gesetzt, wenn die Änderung NICHT übernommen wurde. Der Warenkorb ist dann unverändert. */
  fehler?: WarenkorbFehler
}

/**
 * Zwei Ausprägungen sind dieselbe, wenn alle vier Merkmale übereinstimmen.
 *
 * Ein Produkt kann mehrfach im Warenkorb stehen — einmal je Ausprägung. Ohne diesen Vergleich
 * würden „Größe M" und „Größe L" desselben Artikels zu einer Zeile verschmelzen.
 */
export function gleicheSku(a?: PflegeboxSkuLike, b?: PflegeboxSkuLike): boolean {
  if (a === b) {
    return true
  }
  if (!a || !b) {
    return false
  }
  return a.material === b.material && a.size === b.size && a.productMatchingId === b.productMatchingId && a.quantity === b.quantity
}

/**
 * Der Bruttopreis EINER Einheit dieser Ausprägung.
 *
 * `grossPrice` gilt je Gebinde-Einheit, `quantity` ist die Gebindegröße — ein 100er-Pack Handschuhe
 * kostet das Hundertfache des Stückpreises. Wer den Faktor vergisst, lässt das Vielfache der
 * Pauschale durch.
 */
export function preis(produkt: PflegeboxProduktLike, sku: PflegeboxSkuLike): number {
  return (produkt.grossPrice ?? 0) * (sku.quantity ?? 1)
}

/** Der günstigste Preis über alle Ausprägungen — für „passt das überhaupt noch rein?". */
export function minPreis(produkt: PflegeboxProduktLike): number | undefined {
  const preise = (produkt.sizeMaterialAndQuantityOptions ?? []).map((sku) => preis(produkt, sku))
  return preise.length > 0 ? Math.min(...preise) : undefined
}

/** Bruttosumme des Warenkorbs. */
export function warenkorbSumme(warenkorb: PflegeboxWarenkorbPosition[]): number {
  return warenkorb.reduce((summe, position) => summe + preis(position.produkt, position.sku) * position.menge, 0)
}

/** Was von der Pauschale noch übrig ist. Kann nicht negativ werden, weil nichts darüber hinaus geht. */
export function verfuegbarerBetrag(warenkorb: PflegeboxWarenkorbPosition[]): number {
  return PFLEGEBOX_PAUSCHALE - warenkorbSumme(warenkorb)
}

/** Wie voll die Box ist — 0…1, für die Fortschrittsanzeige. */
export function ausnutzung(warenkorb: PflegeboxWarenkorbPosition[]): number {
  return warenkorbSumme(warenkorb) / PFLEGEBOX_PAUSCHALE
}

/** Die Zeile zu genau dieser Ausprägung, falls sie im Warenkorb liegt. */
export function findePosition(
  warenkorb: PflegeboxWarenkorbPosition[],
  produkt: PflegeboxProduktLike,
  sku?: PflegeboxSkuLike,
): PflegeboxWarenkorbPosition | undefined {
  return warenkorb.find((position) => position.produkt.id === produkt.id && gleicheSku(position.sku, sku))
}

/**
 * Setzt die Menge einer Ausprägung. `menge <= 0` entfernt die Zeile.
 *
 * <p>Gibt **immer** einen neuen Array zurück und mutiert nie — der Warenkorb liegt in einem Signal,
 * und eine Mutation feuert es nicht (Repo-Regel: „`[(ngModel)]` nie auf eine Eigenschaft eines
 * Objekts im Signal"). Wird die Änderung abgelehnt, kommt der **unveränderte** Warenkorb zurück.
 */
export function setzeMenge(
  warenkorb: PflegeboxWarenkorbPosition[],
  produkt: PflegeboxProduktLike,
  sku: PflegeboxSkuLike,
  menge: number,
): WarenkorbErgebnis {
  const index = warenkorb.findIndex((position) => position.produkt.id === produkt.id && gleicheSku(position.sku, sku))

  if (menge <= 0) {
    return { warenkorb: index === -1 ? warenkorb : warenkorb.filter((_, i) => i !== index) }
  }

  // Die Hoechstmenge gilt dem PRODUKT, nicht der einzelnen Zeile: bis 2026-08-25 stand hier
  // `menge > hoechstmenge`, und damit liess sich die Grenze ueber zwei Auspraegungen umgehen — bei
  // `maxAllowedQuantity: 2` ergaben 1x Groesse M und 1x Groesse L zusammen 3. Die Kachel sperrte
  // korrekt (`hoechstmengeErreicht` summierte schon immer ueber alle Auspraegungen), das „+" in der
  // Korbliste nicht; geprueft hat es niemand, weil das Backend `maxAllowedQuantity` gar nicht liest.
  const hoechstmenge = produkt.maxAllowedQuantity ?? Infinity
  const andereAuspraegungen = warenkorb.reduce(
    (summe, position, i) => (i !== index && position.produkt.id === produkt.id ? summe + position.menge : summe),
    0,
  )
  if (andereAuspraegungen + menge > hoechstmenge) {
    return { warenkorb, fehler: 'HOECHSTMENGE_ERREICHT' }
  }

  const neu =
    index === -1
      ? [...warenkorb, { produkt, sku, menge }]
      : warenkorb.map((position, i) => (i === index ? { ...position, menge } : position))

  if (warenkorbSumme(neu) > PFLEGEBOX_PAUSCHALE) {
    return { warenkorb, fehler: 'PAUSCHALE_UEBERSCHRITTEN' }
  }
  return { warenkorb: neu }
}

/** Legt `anzahl` Einheiten dieser Ausprägung dazu (bzw. nimmt sie bei negativem Wert weg). */
export function aendereMenge(
  warenkorb: PflegeboxWarenkorbPosition[],
  produkt: PflegeboxProduktLike,
  sku: PflegeboxSkuLike,
  anzahl: number,
): WarenkorbErgebnis {
  const vorhanden = findePosition(warenkorb, produkt, sku)
  return setzeMenge(warenkorb, produkt, sku, (vorhanden?.menge ?? 0) + anzahl)
}

/**
 * Ob ein weiteres Stück dieses Produkts überhaupt noch hineinpasst — für die Optik der Kachel
 * (ausgegraut statt Fehlermeldung nach dem Klick).
 */
export function passtNochRein(warenkorb: PflegeboxWarenkorbPosition[], produkt: PflegeboxProduktLike): boolean {
  return (minPreis(produkt) ?? Infinity) <= verfuegbarerBetrag(warenkorb)
}

/**
 * Ob die Höchstmenge dieses Produkts über alle seine Ausprägungen erreicht ist.
 *
 * Dieselbe Bezugsgröße wie die Prüfung in {@link setzeMenge} — die beiden liefen bis 2026-08-25
 * auseinander (hier produktweit, dort je Zeile).
 */
export function hoechstmengeErreicht(warenkorb: PflegeboxWarenkorbPosition[], produkt: PflegeboxProduktLike): boolean {
  const hoechstmenge = produkt.maxAllowedQuantity ?? Infinity
  const imKorb = warenkorb.filter((position) => position.produkt.id === produkt.id).reduce((summe, p) => summe + p.menge, 0)
  return imKorb >= hoechstmenge
}
